import { useEffect, useMemo, useState } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { Button, Checkbox, Input, LanguageSelector } from '@iveco-ti/iveco-ds'
import { FormProvider, useForm } from '@iveco-ti/iveco-ds/hook-form'
import Cookies from 'js-cookie'
import { ClockAlert, Eye, EyeOff, GitBranch, Lock } from 'lucide-react'
import ReCAPTCHA from 'react-google-recaptcha'
import { HeadProvider, Title } from 'react-head'
import { useTranslation } from 'react-i18next'
import { Link, Navigate, useSearchParams } from 'react-router'
import { toast } from 'react-toastify'

import BrFlag from '@/assets/flags/BR.svg'
import EsFlag from '@/assets/flags/ES.svg'
import UsFlag from '@/assets/flags/US.svg'
import LogoImg from '@/assets/images/logo.svg?url'
import { Toast } from '@/common/components/Toast'
import { COOKIES_KEYS } from '@/common/constants/cookiesKeys'
import { ENV } from '@/common/constants/env'
import { ROUTES } from '@/common/constants/routes'
import { useModalApplication } from '@/common/hooks/useModalApplication'
import i18n from '@/lib/i18n'
import { useSignIn } from '../../hooks/useSignIn'
import { SignInForm, SignInSchema } from '../../schemas/SignInSchema'

export default function SignIn() {
  const [showPassword, setShowPassword] = useState(false)

  const { t } = useTranslation(['login', 'common'])
  const hookForm = useForm<SignInForm>({
    resolver: zodResolver(SignInSchema()),
    mode: 'onSubmit',
    defaultValues: { username: '', password: '', rememberMe: false },
  })
  const { handleSignIn, isPendingSignIn, reCaptchaRef } = useSignIn()
  const [searchParams, setSearchParams] = useSearchParams()
  const { handleModal, resetModal } = useModalApplication()

  const handleChangeLanguage = (language: string) => {
    i18n.changeLanguage(language)
  }

  const languageOptions = useMemo(() => {
    return [
      {
        label: t('sign_in.languages.options.pt_br'),
        value: 'pt',
        flag: BrFlag,
      },
      {
        label: t('sign_in.languages.options.en_us'),
        value: 'en',
        flag: UsFlag,
      },
      {
        label: t('sign_in.languages.options.es_es'),
        value: 'es',
        flag: EsFlag,
      },
    ]
  }, [t])

  const CURRENT_LANGUAGE =
    languageOptions.find((option) => option.value === i18n.language) ||
    languageOptions.find((option) => option.value === 'pt')

  useEffect(() => {
    const disconnected = searchParams.get('disconnected')

    if (disconnected) {
      setSearchParams()
      toast(Toast, {
        toastId: 'disconnected-toast',
        data: {
          type: 'info',
          icon: ClockAlert,
          title: t('sign_in.toast.disconnected.title'),
          description: t('sign_in.toast.disconnected.message'),
        },
      })
    }
  }, [searchParams, setSearchParams, t])

  const USER_LOGGED = Cookies.get(COOKIES_KEYS.token)

  if (USER_LOGGED) {
    return <Navigate to={ROUTES.PANEL.NEWS} replace />
  }

  return (
    <div className="flex flex-col gap-28 p-3 h-full w-full overflow-y-auto">
      <HeadProvider>
        <Title>{t('sign_in.browser.title')}</Title>
      </HeadProvider>

      <img
        src={LogoImg}
        alt={t('sign_in.logo.alt')}
        className="w-2/4 mx-auto"
      />

      <FormProvider {...hookForm}>
        <form
          onSubmit={hookForm.handleSubmit(handleSignIn)}
          className="flex flex-col gap-6"
        >
          <Input.Base
            size="lg"
            name="username"
            placeholder={t('sign_in.email.placeholder')}
          />
          <Input.Icon
            size="lg"
            name="password"
            placeholder={t('sign_in.password.placeholder')}
            icon={
              showPassword ? (
                <Eye
                  className="text-base-500"
                  onClick={() => setShowPassword(false)}
                />
              ) : (
                <EyeOff
                  className="text-base-500"
                  onClick={() => setShowPassword(true)}
                />
              )
            }
            type={showPassword ? 'text' : 'password'}
            iconPosition="right"
          />
          <div className="flex w-full justify-around items-center">
            <div className="text-brand-900 text-sm">
              <Checkbox
                name="rememberMe"
                text={t('sign_in.remember_me.text')}
              />
            </div>

            <div className="h-full border border-base-200 rounded-full" />

            <Link
              to={ROUTES.LOGIN.FORGOT_PASSWORD}
              className="text-brand-400 text-sm flex gap-2 items-center"
            >
              <Lock className="text-brand-600 size-4 stroke-3" />
              {t('sign_in.forgot_password.text')}
            </Link>
          </div>
          <div className="mt-20">
            <Button.Root
              type="submit"
              size="lg"
              block
              disabled={isPendingSignIn}
              isLoading={isPendingSignIn}
            >
              {t('sign_in.button.text')}
            </Button.Root>
          </div>

          <div className="w-fit mx-auto">
            <LanguageSelector
              options={languageOptions}
              placeholder={t('sign_in.languages.placeholder')}
              selected={CURRENT_LANGUAGE}
              onSelect={handleChangeLanguage}
            />
          </div>
        </form>
      </FormProvider>

      <div className="flex flex-col gap-4">
        {ENV.VITE_RECAPTCHA_ENABLED && (
          <ReCAPTCHA
            ref={reCaptchaRef}
            sitekey={ENV.VITE_RECAPTCHA_SITE_KEY}
            size="invisible"
            className="absolute"
          />
        )}

        <div className="flex items-center justify-center gap-1 text-sm">
          <button
            onClick={() =>
              handleModal({
                title: t('common:app_connection.terms.title'),
                description: (
                  <div className="bg-gray-50 p-4 rounded-md border max-h-[400px] overflow-y-auto">
                    <div className="text-sm text-gray-600 whitespace-pre-wrap">
                      {t('common:app_connection.terms.content')}
                    </div>
                  </div>
                ),
                confirmText: t('common:app_connection.buttons.close'),
                showButtons: true,
                onConfirm: resetModal,
              })
            }
            className="text-brand-400 hover:underline"
          >
            {t('sign_in.footer.terms_of_use.text')}
          </button>

          <span className="text-base-400">{t('sign_in.footer.separator')}</span>

          <button
            onClick={() =>
              handleModal({
                title: t('common:app_connection.privacy.title'),
                description: (
                  <div className="bg-gray-50 p-4 rounded-md border max-h-[400px] overflow-y-auto">
                    <div className="text-sm text-gray-600 whitespace-pre-wrap">
                      {t('common:app_connection.privacy.content')}
                    </div>
                  </div>
                ),
                confirmText: t('common:app_connection.buttons.close'),
                showButtons: true,
                onConfirm: resetModal,
              })
            }
            className="text-brand-400 hover:underline"
          >
            {t('sign_in.footer.privacy_policy.text')}
          </button>
        </div>

        <p className="text-base-400 font-light text-xs text-center">
          {t('sign_in.footer.copyright.text')}
        </p>

        <p className="text-center text-xs text-base-400">
          Powered by{' '}
          <a
            href="https://ideros.com.br/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-brand-400 hover:underline"
          >
            IDEROS
          </a>
        </p>

        <div className="flex items-center justify-center gap-1 w-full">
          <GitBranch className="size-3 text-base-400" />

          <p className="text-base-400 font-light text-xs text-center">
            v{import.meta.env.APP_VERSION}
          </p>
        </div>
      </div>
    </div>
  )
}
